import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, AtSign, Users as UsersIcon, MessagesSquare, CheckCheck, Radio } from "lucide-react";

const TABS = [
  { key: "all", label: "All", icon: Bell },
  { key: "mention", label: "Mentions", icon: AtSign },
  { key: "lead", label: "Leads", icon: UsersIcon },
  { key: "comms", label: "Comms", icon: MessagesSquare },
  { key: "transmission", label: "Transmissions", icon: Radio },
];
const CAT_ICON = { mention: AtSign, lead: UsersIcon, comms: MessagesSquare, transmission: Radio, other: Bell };

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Shared notification feed used by the Mission Console header bell and the Inbox page,
// so both show the exact same items and tabs (All · Mentions · Leads · Comms).
export function useAgencyNotifFeed(poll = false) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, mention: 0, lead: 0, comms: 0 });
  const [unread, setUnread] = useState(0);
  const timer = useRef(null);
  const load = async () => {
    try {
      const { data } = await api.get("/console/notifications/feed");
      setItems(data.notifications || []); setCounts(data.counts || {}); setUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    load();
    if (poll) { timer.current = setInterval(load, 30000); return () => clearInterval(timer.current); }
  }, [poll]);
  return { items, counts, unread, load, setUnread };
}

export function AgencyNotifications({ feed, onNavigate, dense = false }) {
  const [tab, setTab] = useState("all");
  const { items, counts, unread, load } = feed;
  const shown = tab === "all" ? items : items.filter((n) => n.category === tab);
  const markAll = async () => { try { await api.post("/console/notifications/feed/read-all"); load(); } catch (e) { /* ignore */ } };
  const open = async (n) => {
    if (!n.read) { api.post(`/console/notifications/feed/read/${n.id}`).catch(() => {}); }
    if (onNavigate) onNavigate(n);
    setTimeout(load, 300);
  };
  return (
    <div data-testid="agency-notifications">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#f0f0f3]">
        <span className="text-sm font-medium text-[#06060C]">Notifications</span>
        {unread > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="inline-flex items-center gap-1 text-[11px] text-[#5b2fd6] hover:text-[#06060C]"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>}
      </div>
      <div className="flex items-center gap-1 px-2.5 py-1.5 border-b border-[#f7f7f9] flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key; const cnt = counts[t.key] || 0;
          return (
            <button key={t.key} data-testid={`notif-tab-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${active ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>
              <Icon className="h-3 w-3" /> {t.label}{cnt > 0 && <span className={`ml-0.5 rounded-full px-1 text-[9px] ${active ? "bg-white/25 text-white" : "bg-[#EDE7FF] text-[#5b2fd6]"}`}>{cnt}</span>}
            </button>
          );
        })}
      </div>
      <div className={`overflow-y-auto ${dense ? "max-h-80" : "max-h-[62vh]"}`}>
        {shown.length === 0 ? (
          <p className="text-xs text-[#9FA1A4] text-center py-10">{tab === "all" ? "You're all caught up." : `No ${TABS.find((t) => t.key === tab)?.label.toLowerCase()} yet.`}</p>
        ) : shown.map((n) => {
          const Icon = CAT_ICON[n.category] || Bell;
          return (
            <button key={n.id} data-testid="notif-item" onClick={() => open(n)} className={`w-full text-left flex gap-3 px-3 py-2.5 border-b border-[#f7f7f9] hover:bg-[#F6F6FD] transition-colors ${n.read ? "" : "bg-[#F9F6FF]"}`}>
              <span className="h-8 w-8 rounded-xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-[#06060C] truncate">{n.title}</span>
                {n.body && <span className="block text-[11px] text-[#9FA1A4] truncate">{n.body}</span>}
                <span className="block text-[10px] text-[#c4c5cc] mt-0.5">{n.client?.name || "Mission Console"} · {timeAgo(n.created_at)}</span>
              </span>
              {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#723FFF] mt-1.5 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
